import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { skip, switchMap, tap } from 'rxjs/operators';

import { RouterFacade } from '@core/shared/data-access';
import { NotificationService } from '@core/shared/util';
import {
  CreatePlatformCatalog,
  DEFAULT_PAGINATION,
  MarkPublishFilter,
  PlatformCatalog,
  PlatformCatalogAssignedOrganization,
  PlatformCatalogAvailableOrganization,
  PlatformCatalogFilter,
  PlatformCatalogGlobalFilter,
  PlatformIndustryCatalogFilter,
  PlatformcatalogSearchQueryField,
  PlattformkatalogeService,
  UpdatePlatformCatalog,
} from '@mp/content-manager/plattformkataloge/data-access';
import { QueryParamsStoreFilterFacade } from '@mp/shared/data-access';

@Injectable()
export class PlattformkatalogeFacade {
  private readonly _isLoading$ = new BehaviorSubject(false as boolean);
  readonly isLoading$ = this._isLoading$.asObservable();

  private readonly _kataloge$ = new BehaviorSubject([] as Array<PlatformCatalog>);
  readonly kataloge$ = this._kataloge$.asObservable();

  private readonly _dataSources$ = new BehaviorSubject([] as Array<string>);
  readonly dataSources$ = this._dataSources$.asObservable();

  // Variables for the organization selector
  private readonly _catalogOrganizations$ = new BehaviorSubject([] as Array<PlatformCatalogAssignedOrganization>);
  readonly catalogOrganizations$ = this._catalogOrganizations$.asObservable();

  private readonly _availableOrganizations$ = new BehaviorSubject<PlatformCatalogAvailableOrganization[]>([]);
  readonly availableOrganizations$ = this._availableOrganizations$.asObservable();

  private readonly _industryCatalogFilters = new Map<string, PlatformIndustryCatalogFilter>();

  constructor(
    private readonly service: PlattformkatalogeService,
    private readonly toaster: NotificationService,
    private readonly filterFacade: QueryParamsStoreFilterFacade<PlatformCatalogFilter>,
    private readonly routerFacade: RouterFacade,
  ) {}

  getAllPlattformKataloge(filterOptions: PlatformCatalogGlobalFilter = this.filterFacade.filter): void {
    this._isLoading$.next(true);

    this.service.getPlattformkataloge(filterOptions).subscribe({
      next: (data) => {
        this._kataloge$.next(data);
        this._isLoading$.next(false);
      },
    });
  }

  getAllDataSources(): void {
    this.service.getAllDataSources().subscribe({
      next: (data) => {
        this._dataSources$.next(data);
      },
    });
  }

  getPlatformCatalogById(catalogId: string): Observable<PlatformCatalog> {
    function getCatalog(catalogs: readonly PlatformCatalog[]) {
      const platformCatalog = catalogs.find((c) => c.catalogId === catalogId);
      if (platformCatalog) {
        return of(platformCatalog);
      }

      return throwError(() => new Error('Plattformkatalog nicht gefunden'));
    }

    return this._kataloge$.pipe(
      skip(this._isLoading$.getValue() ? 1 : 0),
      switchMap((catalogs) => getCatalog(catalogs)),
    );
  }

  createPlattformKatalog(plattformKatalog: CreatePlatformCatalog): Observable<PlatformCatalog> {
    return this.service.createPlattformkatalog(plattformKatalog).pipe(
      tap(() => {
        this.toaster.toastSuccess('Der Plattformkatalog wurde erfolgreich angelegt.');
        this.getAllPlattformKataloge();
      }),
    );
  }

  publishPlattformKatalog(id: string): void {
    this.service
      .publishPlattformkatalog(id)
      .pipe(tap(() => this.refreshPlatformCatalogs()))
      .subscribe();
  }

  markKatalogeForPublishing(
    plattformkatalogId: string,
    publish: boolean,
    industrieKatalogIds: Array<string>,
    filter?: MarkPublishFilter,
  ): void {
    this.service
      .markIndustriekatalogeForPublishing(plattformkatalogId, publish, industrieKatalogIds, filter)
      .subscribe();
  }

  reindexPlattformkatalog(catalogId: string): void {
    this.service
      .reindexPlattformkatalog(catalogId)
      .pipe(tap(() => this.refreshPlatformCatalogs()))
      .subscribe({
        next: () => {
          this.toaster.toastSuccess('Die Reindexierung des Plattformkatalogs wurde gestartet.');
        },
      });
  }

  updatePlatformCatalog(catalogId: string, dto: UpdatePlatformCatalog): Observable<PlatformCatalog> {
    return this.service.updatePlatformCatalog(catalogId, dto).pipe(
      tap((platformCatalog) => {
        const existingCatalogs = this._kataloge$.getValue();
        const index = existingCatalogs.findIndex((x) => x.catalogId === platformCatalog.catalogId);
        if (index >= 0) {
          this._kataloge$.next([
            ...existingCatalogs.slice(0, index),
            platformCatalog,
            ...existingCatalogs.slice(index + 1),
          ]);
        }
      }),
    );
  }

  deletePlatformCatalog(catalogId: string): Observable<void> {
    return this.service.deletePlatformCatalog(catalogId).pipe(
      tap(() => {
        const existingCatalogs = this._kataloge$.getValue();
        const index = existingCatalogs.findIndex((x) => x.catalogId === catalogId);
        if (index >= 0) {
          this._kataloge$.next([...existingCatalogs.slice(0, index), ...existingCatalogs.slice(index + 1)]);
        }
      }),
    );
  }

  navigateToIndustryCatalogDetail(id: string): void {
    this.routerFacade.navigate((router) =>
      router.navigate(['/', 'katalog-management', 'kataloge', encodeURIComponent(id)]),
    );
  }

  private refreshPlatformCatalogs(): void {
    this.service.getPlattformkataloge(this.filterFacade.filter).subscribe({
      next: (data) => {
        this._kataloge$.next(data);
      },
    });
  }

  // The following methods are to manage platformcatalog organizations (mp-selector):
  getCatalogOrganisationsIds(): Array<string> {
    return this._catalogOrganizations$.value.map((organization) => organization.organizationId);
  }

  loadCatalogOrganizations(platformCatalogId: string): void {
    const field = this.filterFacade.filter.field;

    // Use search term only if organization relevant field is selected
    const searchTerm =
      field === PlatformcatalogSearchQueryField.All || field === PlatformcatalogSearchQueryField.OrganizationName
        ? this.filterFacade.filter.searchTerm
        : undefined;

    this.service.getPlatformCatalogOrganizations(platformCatalogId, searchTerm).subscribe({
      next: (organizations) => this._catalogOrganizations$.next(organizations),
    });
  }

  loadAvailableOrganizations(): void {
    this.service.getAvailableOrganizations().subscribe({
      next: (organizations) => this._availableOrganizations$.next(organizations),
    });
  }

  addOrganisations(platformCatalogId: string, ...organizationIds: Array<string>): void {
    const currentOrganizationIds = new Set(
      this._catalogOrganizations$
        .getValue()
        .filter((o) => !o.isRemoved)
        .map((o) => o.organizationId),
    );

    for (const org of organizationIds) {
      currentOrganizationIds.add(org);
    }

    this.setCatalogOrganizations(platformCatalogId, Array.from(currentOrganizationIds.values()));
  }

  removeOrganisation(platformCatalogId: string, organizationId: string): void {
    const currentOrganizationIds = new Set(
      this._catalogOrganizations$
        .getValue()
        .filter((o) => !o.isRemoved)
        .map((o) => o.organizationId),
    );

    currentOrganizationIds.delete(organizationId);

    this.setCatalogOrganizations(platformCatalogId, Array.from(currentOrganizationIds.values()));
  }

  private setCatalogOrganizations(platformCatalogId: string, organizationIds: string[]): void {
    this.service.setOrganizations(platformCatalogId, organizationIds).subscribe({
      next: (assignedOrganizations) => {
        this._catalogOrganizations$.next(assignedOrganizations);

        const availableOrganizations = new Map(
          this._availableOrganizations$.getValue().map((o) => [o.organizationId, o]),
        );

        for (const org of assignedOrganizations) {
          availableOrganizations.delete(org.organizationId);
        }

        this._availableOrganizations$.next(Array.from(availableOrganizations.values()));

        const platformCatalogs = [...this._kataloge$.getValue()];
        const platformCatalogIndex = platformCatalogs.findIndex((c) => c.catalogId === platformCatalogId);
        if (platformCatalogIndex > -1) {
          platformCatalogs[platformCatalogIndex] = {
            ...platformCatalogs[platformCatalogIndex],
            hasUnpublishedChanges: true,
            assignedOrganizationsCount: assignedOrganizations.length,
          };
          this._kataloge$.next(platformCatalogs);
        }
      },
      error: () => {
        this.toaster.toastDanger('Fehler beim Zuordnen der Organisationen');
      },
    });
  }

  getFilterQueryParamsForPlatformCatalog(platformCatalogId: string) {
    const filter = this.updateAndGetFilterForPlatformCatalog(platformCatalogId);
    return this.filterFacade.queryParamsFilter.convertToQueryParams(filter);
  }

  updateGlobalFilter(filter: Partial<PlatformCatalogGlobalFilter>): void {
    if (!this.filterFacade.hasChangedFilterState(filter)) {
      return;
    }

    let updatedFilter = {
      ...this.filterFacade.filter,
      ...filter,
    } satisfies PlatformCatalogFilter;

    if ('searchTerm' in filter) {
      updatedFilter = {
        ...updatedFilter,
        ...{
          ...DEFAULT_PAGINATION,
          pageSize: updatedFilter.pageSize,
          page: 1,
        },
      };
      this._industryCatalogFilters.clear();
    }

    this.filterFacade.updateFilter(updatedFilter);
  }

  updateIndustryCatalogFilter(platformCatalogId: string, filter: Partial<PlatformIndustryCatalogFilter>): void {
    const updatedFilter = this.updateAndGetFilterForPlatformCatalog(platformCatalogId, filter);
    this.filterFacade.updateFilter(updatedFilter);
  }

  updateAndGetFilterForPlatformCatalog(
    platformCatalogId: string,
    updates?: Partial<PlatformIndustryCatalogFilter>,
  ): PlatformCatalogFilter {
    const currentFilter = this._industryCatalogFilters.get(platformCatalogId) ?? {};

    const updatedFilter = {
      ...DEFAULT_PAGINATION,
      ...currentFilter,
      ...updates,
    } satisfies PlatformIndustryCatalogFilter;

    this._industryCatalogFilters.set(platformCatalogId, updatedFilter);

    return {
      ...this.filterFacade.filter,
      ...updatedFilter,
    };
  }

  resetFilter(): void {
    this.filterFacade.resetFilter();
  }
}
